import type { FC } from 'react';

import MetricCard from '@/common-components/MetricCard';
import type { DashboardOverview, MetricDelta } from '@/types/stats.types';
import { formatRupeesCompact } from '@/utils/currency.util';

interface DashboardMetricsProps {
  overview: DashboardOverview;
  onOpenOnboardingQueue: () => void;
  onOpenReports: () => void;
}

const NUM = new Intl.NumberFormat('en-IN');

/**
 * Renders the prototype's `↑ 12.4% vs last week` line from a real comparison.
 *
 * Returns nothing when there is no previous window to compare against — a card with no history
 * shows no change line rather than a fabricated one.
 */
const changeFor = (
  delta: MetricDelta,
  suffix: string,
): { change?: string; changeTone: 'up' | 'down' | 'neutral' } => {
  if (delta.previous === null) return { changeTone: 'neutral' };
  if (delta.previous === 0) {
    return delta.value === 0
      ? { changeTone: 'neutral' }
      : { change: `↑ new ${suffix}`, changeTone: 'up' };
  }
  const pct = ((delta.value - delta.previous) / delta.previous) * 100;
  const rounded = Math.round(Math.abs(pct) * 10) / 10;
  if (rounded === 0) return { change: `No change ${suffix}`, changeTone: 'neutral' };
  return {
    change: `${pct >= 0 ? '↑' : '↓'} ${rounded}% ${suffix}`,
    changeTone: pct >= 0 ? 'up' : 'down',
  };
};

const DashboardMetrics: FC<DashboardMetricsProps> = ({
  overview,
  onOpenOnboardingQueue,
  onOpenReports,
}) => (
  <>
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <MetricCard
        icon="👥"
        label="Total Users"
        value={NUM.format(overview.totalUsers.value)}
        accent="brand"
        {...changeFor(overview.totalUsers, 'vs last week')}
      />
      <MetricCard
        icon="📦"
        label="Active Rentals"
        value={NUM.format(overview.activeRentals.value)}
        accent="success"
        change="Out with borrowers now"
      />
      <MetricCard
        icon="📣"
        label="Active Shoutts"
        value={NUM.format(overview.activeShoutts.value)}
        accent="info"
        change="Live on campus"
      />
      <MetricCard
        icon="💰"
        label="Revenue (MTD)"
        value={formatRupeesCompact(overview.revenueMtdPaise.value)}
        accent="pink"
        {...changeFor(overview.revenueMtdPaise, 'vs last month')}
      />
    </div>

    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <MetricCard
        icon="🪙"
        label="Coin Economy"
        value={NUM.format(overview.coinsInCirculation)}
        accent="brand"
        change="Coins in circulation"
      />
      <MetricCard
        icon="⚡"
        label="New Signups (7d)"
        value={NUM.format(overview.newSignups7d.value)}
        accent="success"
        {...changeFor(overview.newSignups7d, 'vs last week')}
      />
      <MetricCard
        icon="🌱"
        label="Incomplete Onboarding"
        value={NUM.format(overview.incompleteOnboarding.value)}
        accent="warn"
        change={
          overview.incompleteThisWeek > 0
            ? `${overview.incompleteThisWeek} new this week`
            : 'Nobody new this week'
        }
        changeTone={overview.incompleteThisWeek > 0 ? 'down' : 'neutral'}
        onClick={onOpenOnboardingQueue}
      />
      <MetricCard
        icon="⚠️"
        label="Open Reports"
        value={NUM.format(overview.openReports.value)}
        accent="danger"
        change={overview.openReports.value > 0 ? 'Awaiting review' : 'All clear'}
        changeTone={overview.openReports.value > 0 ? 'down' : 'up'}
        onClick={onOpenReports}
      />
    </div>
  </>
);

export default DashboardMetrics;
